import { useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import {
  CheckCircle, AlertCircle, Loader2,
  HelpCircle, Users, UserPlus, Home, Heart, CreditCard, FileText
} from 'lucide-react';
import { Button } from '~/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import { StatusCard } from '~/components/StatusCard';
import { WizardFormLayout } from '~/components/forms/WizardFormLayout';
import { useMultiStepForm, type StepDefinition } from '~/hooks/useMultiStepForm';
import {
  IntentStep,
  SelectStudentsStep,
  NewStudentsStep,
  FamilyInfoStep,
  StudentMedicalStep,
  TuitionStep,
  ReviewStep
} from './steps';
import { GetReenrollmentFormMutation } from './useGetReenrollmentForm';
import { SaveReenrollmentFormDraftMutation } from './useSaveReenrollmentFormDraft';
import { SubmitReenrollmentFormMutation } from './useSubmitReenrollmentForm';
import LinkReplaced from './LinkReplaced';
import { linkReplacedSchoolName } from './linkReplacedError';

type ReenrollmentStepId = 'intent' | 'students' | 'new-students' | 'family' | 'medical' | 'tuition' | 'review';

const STEPS: StepDefinition<ReenrollmentStepId>[] = [
  { id: 'intent', label: 'Intent', icon: HelpCircle },
  { id: 'students', label: 'Students', icon: Users },
  { id: 'new-students', label: 'New Students', icon: UserPlus },
  { id: 'family', label: 'Family Info', icon: Home },
  { id: 'medical', label: 'Medical', icon: Heart },
  { id: 'tuition', label: 'Tuition', icon: CreditCard },
  { id: 'review', label: 'Review', icon: FileText },
];

function ReenrollmentForm() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [replacedBy, setReplacedBy] = useState<string | null>(null);

  // Track whether step reviews are complete (for steps with tabs)
  const [familyInfoReviewed, setFamilyInfoReviewed] = useState(false);
  const [medicalReviewed, setMedicalReviewed] = useState(false);

  const [getForm] = useMutation(GetReenrollmentFormMutation);
  const [saveDraft] = useMutation(SaveReenrollmentFormDraftMutation);
  const [submitForm, { loading: submitting }] = useMutation(SubmitReenrollmentFormMutation);

  const loadForm = useCallback(async () => {
    let result;
    try {
      result = await getForm({ variables: { token: token! } });
    } catch (err) {
      const schoolName = linkReplacedSchoolName(err);
      if (schoolName) setReplacedBy(schoolName);
      throw err;
    }
    const formResult = result.data!.getReenrollmentForm!;
    const answers = formResult.formAnswers as any;
    const userErrors = formResult.userErrors;

    if (userErrors && userErrors.length > 0) {
      const errorMessage = userErrors[0].message;
      const packetStates = ['PACKET_SENT', 'PACKET_VIEWED', 'PACKET_SUBMITTED', 'PACKET_COMPLETED'];
      const isInPacketPhase = packetStates.some((state: string) => errorMessage.includes(state));
      if (isInPacketPhase) {
        navigate(`/app/packet/${token}`, { replace: true });
        throw new Error('__redirect__');
      }
      throw new Error(errorMessage);
    }

    // Restore reviewed states from saved answers
    if (answers?.reviewedSteps?.familyInfo) {
      setFamilyInfoReviewed(true);
    }
    if (answers?.reviewedSteps?.medical) {
      setMedicalReviewed(true);
    }

    // Build metadata entirely from live GraphQL fields (no longer reading from frozen metadata)
    const enhancedMetadata = {
      schoolYear: {
        name: formResult.schoolYearName,
        startDate: formResult.schoolYearStartDate,
        endDate: formResult.schoolYearEndDate,
      },
      family: { familyName: formResult.familyName },
      alreadyPersistedNewStudents: formResult.persistedNewStudents || [],
      availableOptions: {
        tuitionOptions: formResult.tuitionOptions || [],
        paymentOptions: formResult.paymentOptions || [],
        enrollmentFee: formResult.enrollmentFee || {},
        gradeLevels: formResult.gradeLevels || [],
      },
    };

    return { metadata: enhancedMetadata, answers };
  }, [token, getForm, navigate]);

  const saveDraftFn = useCallback(async (answers: any, stepIndex: number) => {
    await saveDraft({
      variables: {
        token: token!,
        formData: answers,
        currentStep: stepIndex
      }
    });
  }, [token, saveDraft]);

  const getVisibleSteps = useCallback((answers: any) => {
    if (answers?.intent?.isReenrolling === false) {
      return [STEPS[0]];
    }
    return STEPS;
  }, []);

  const validateStep = useCallback((stepId: ReenrollmentStepId, answers: any, metadata: any) => {
    const tuitionOptions = metadata?.availableOptions?.tuitionOptions || [];
    const paymentOptions = metadata?.availableOptions?.paymentOptions || [];

    switch (stepId) {
      case 'intent':
        return answers?.intent?.isReenrolling !== undefined && answers?.intent?.isReenrolling !== null;
      case 'students':
        return (answers?.returningStudents || []).some((s: any) => s.isReturning !== false);
      case 'family':
        return familyInfoReviewed;
      case 'medical':
        return medicalReviewed && !!answers?.medicalConsentSignature?.trim();
      case 'tuition': {
        if (tuitionOptions.length === 0 || paymentOptions.length === 0) return true;
        const persistedNew = (answers?.persistedNewStudents || []).filter((s: any) => s.firstName || s.lastName);
        const unpersistedNew = (answers?.unpersistedNewStudents || []).filter((s: any) => s.firstName || s.lastName);
        const toRemove = answers?.persistedNewStudentsToRemove || [];
        const activePersistedNew = persistedNew.filter((s: any) => !toRemove.includes(s.studentEnrollmentId));
        const enrollingStudentIds = [
          ...(answers?.returningStudents || []).filter((s: any) => s.isReturning).map((s: any) => s.studentEnrollmentId),
          ...activePersistedNew.map((s: any) => s.studentEnrollmentId),
          ...unpersistedNew.map((s: any) => s.tempId)
        ];
        if (enrollingStudentIds.length === 0) return true;
        const studentSelections = answers?.tuitionPreferences?.studentSelections || {};
        const familyPaymentOptionKey = answers?.tuitionPreferences?.paymentOptionKey;
        const allStudentsHaveTuition = enrollingStudentIds.every((id: string) => studentSelections[id]?.tuitionOptionKey);
        return allStudentsHaveTuition && !!familyPaymentOptionKey;
      }
      case 'review': {
        const hasAcknowledgments = answers?.acknowledgments?.infoAccurate && answers?.acknowledgments?.understandPolicy;
        const hasMedicalConsentSignature = !!answers?.medicalConsentSignature?.trim();
        if (tuitionOptions.length === 0 || paymentOptions.length === 0) {
          return hasAcknowledgments && hasMedicalConsentSignature;
        }
        const allStudentIds = [
          ...(answers?.returningStudents || []).filter((s: any) => s.isReturning).map((s: any) => s.studentEnrollmentId),
          ...(answers?.newStudents || []).filter((s: any) => s.firstName || s.lastName).map((s: any) => s.tempId)
        ];
        const allSelections = answers?.tuitionPreferences?.studentSelections || {};
        const reviewFamilyPaymentKey = answers?.tuitionPreferences?.paymentOptionKey;
        const allHaveTuition = allStudentIds.every((id: string) => allSelections[id]?.tuitionOptionKey);
        return hasAcknowledgments && allHaveTuition && !!reviewFamilyPaymentKey && hasMedicalConsentSignature;
      }
      default:
        return true;
    }
  }, [familyInfoReviewed, medicalReviewed]);

  const form = useMultiStepForm<ReenrollmentStepId>({
    steps: STEPS,
    loadForm,
    saveDraft: saveDraftFn,
    getVisibleSteps,
    validateStep,
  });

  // Sync reviewed states to formAnswers for persistence
  const handleFamilyInfoReviewed = useCallback((reviewed: boolean) => {
    setFamilyInfoReviewed(reviewed);
    if (reviewed && form.answers && !form.answers.reviewedSteps?.familyInfo) {
      form.setAnswers((prev: any) => ({
        ...prev,
        reviewedSteps: { ...prev?.reviewedSteps, familyInfo: true }
      }));
    }
  }, [form.answers, form.setAnswers]);

  const handleMedicalReviewed = useCallback((reviewed: boolean) => {
    setMedicalReviewed(reviewed);
    if (reviewed && form.answers && !form.answers.reviewedSteps?.medical) {
      form.setAnswers((prev: any) => ({
        ...prev,
        reviewedSteps: { ...prev?.reviewedSteps, medical: true }
      }));
    }
  }, [form.answers, form.setAnswers]);

  // Submit handlers
  const handleDecline = useCallback(async () => {
    try {
      const result = await submitForm({ variables: { token: token!, formData: form.answers } });
      const { userErrors } = result.data!.submitReenrollmentForm!;
      if (userErrors && userErrors.length > 0) {
        form.setError(userErrors[0].message);
        return;
      }
      setSubmitted(true);
      setShowDeclineDialog(false);
    } catch (err: any) {
      form.setError(err.message);
    }
  }, [token, form.answers, form.setError, submitForm]);

  const handleSubmit = useCallback(async () => {
    try {
      const result = await submitForm({ variables: { token: token!, formData: form.answers } });
      const { userErrors } = result.data!.submitReenrollmentForm!;
      if (userErrors && userErrors.length > 0) {
        form.setError(userErrors[0].message);
        return;
      }
      setSubmitted(true);
      setShowSubmitDialog(false);
    } catch (err: any) {
      form.setError(err.message);
    }
  }, [token, form.answers, form.setError, submitForm]);

  // Derived
  const schoolYearName = form.metadata?.schoolYear?.name || 'Upcoming Year';
  const tuitionOptions = useMemo(() => form.metadata?.availableOptions?.tuitionOptions || [], [form.metadata?.availableOptions?.tuitionOptions]);
  const paymentOptions = useMemo(() => form.metadata?.availableOptions?.paymentOptions || [], [form.metadata?.availableOptions?.paymentOptions]);
  const gradeLevels = form.metadata?.availableOptions?.gradeLevels || [];
  const schoolYearStartDate = form.metadata?.schoolYear?.startDate || null;
  const schoolYearEndDate = form.metadata?.schoolYear?.endDate || null;

  // Combine metadata and answers for step components
  const formData = useMemo(() => {
    if (!form.metadata || !form.answers) return null;
    return {
      intent: form.answers.intent,
      returningStudents: form.answers.returningStudents,
      persistedNewStudents: form.answers.persistedNewStudents || [],
      unpersistedNewStudents: form.answers.unpersistedNewStudents || [],
      persistedNewStudentsToRemove: form.answers.persistedNewStudentsToRemove || [],
      newStudents: form.answers.unpersistedNewStudents || [],
      familyInfo: form.answers.familyInfo,
      studentMedicalInfo: form.answers.studentMedicalInfo,
      medicalConsentSignature: form.answers.medicalConsentSignature,
      tuitionPreferences: form.answers.tuitionPreferences,
      acknowledgments: form.answers.acknowledgments,
      currentStep: form.answers.currentStep,
      submittedAt: form.answers.submittedAt,
      availableOptions: {
        ...form.metadata.availableOptions,
        alreadyPersistedNewStudents: form.metadata.alreadyPersistedNewStudents || []
      },
      schoolYear: form.metadata.schoolYear,
    };
  }, [form.metadata, form.answers]);

  // Loading
  if (form.loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading your enrollment form...</p>
        </div>
      </div>
    );
  }

  if (replacedBy) return <LinkReplaced schoolName={replacedBy} />;

  // Error
  if (form.error) {
    return (
      <StatusCard
        icon={<AlertCircle className="h-5 w-5" />}
        title="Unable to Load Form"
        titleClassName="text-destructive"
        footer={
          <Button variant="outline" onClick={() => navigate('/app')}>
            Go to Home
          </Button>
        }
      >
        <p className="text-muted-foreground">{form.error}</p>
      </StatusCard>
    );
  }

  // Submitted - declined
  if (submitted && form.answers?.intent?.isReenrolling === false) {
    return (
      <StatusCard
        icon={<CheckCircle className="h-5 w-5" />}
        title="Response Submitted"
        titleClassName="text-primary"
        footer={
          <Button variant="outline" onClick={() => navigate('/app')}>
            Done
          </Button>
        }
      >
        <p className="text-muted-foreground">
          Thank you for letting us know. We wish your family all the best in the future.
          If your plans change, please contact the school office.
        </p>
      </StatusCard>
    );
  }

  // Submitted - enrolled
  if (submitted) {
    return (
      <StatusCard
        icon={<CheckCircle className="h-5 w-5" />}
        title="Re-enrollment Submitted"
        titleClassName="text-green-600"
        footer={
          <Button variant="outline" onClick={() => navigate('/app')}>
            Done
          </Button>
        }
      >
        <p className="text-muted-foreground">
          Thank you for completing your re-enrollment for {schoolYearName}!
          The school will review your submission and send you an enrollment packet with next steps.
        </p>
      </StatusCard>
    );
  }

  const familyName = form.metadata?.family?.familyName || 'Your Family';
  const isReenrolling = form.answers?.intent?.isReenrolling;
  const showDeclineSubmit = isReenrolling === false && form.currentStepId === 'intent';

  return (
    <>
      <WizardFormLayout
        title="Re-enrollment Confirmation"
        subtitle={`${familyName} - ${schoolYearName}`}
        savingIndicator={{ saving: form.saving, lastSaved: form.lastSaved }}
        steps={form.visibleSteps}
        currentStep={form.currentStep}
        onStepClick={form.goToStep}
        canProceed={form.canProceed}
        isFirstStep={form.isFirstStep}
        isLastStep={form.isLastStep}
        onNext={showDeclineSubmit ? () => setShowDeclineDialog(true) : form.isLastStep ? () => setShowSubmitDialog(true) : form.nextStep}
        onPrev={form.prevStep}
        submitLabel={showDeclineSubmit ? 'Submit Response' : 'Submit Re-enrollment'}
        onSubmit={showDeclineSubmit ? () => setShowDeclineDialog(true) : () => setShowSubmitDialog(true)}
        submitting={submitting}
      >
        {form.currentStepId === 'intent' && (
          <IntentStep
            formData={formData}
            schoolYearName={schoolYearName}
            updateFormData={form.updateAnswers}
          />
        )}
        {form.currentStepId === 'students' && (
          <SelectStudentsStep
            formData={formData}
            gradeLevels={gradeLevels}
            updateFormData={form.updateAnswers}
          />
        )}
        {form.currentStepId === 'new-students' && (
          <NewStudentsStep
            formData={formData}
            gradeLevels={gradeLevels}
            updateFormData={form.updateAnswers}
          />
        )}
        {form.currentStepId === 'family' && (
          <FamilyInfoStep
            formData={formData}
            updateFormData={form.updateAnswers}
            onReviewComplete={handleFamilyInfoReviewed}
          />
        )}
        {form.currentStepId === 'medical' && (
          <StudentMedicalStep
            formData={formData}
            updateFormData={form.updateAnswers}
            onReviewComplete={handleMedicalReviewed}
          />
        )}
        {form.currentStepId === 'tuition' && (
          <TuitionStep
            formData={formData}
            tuitionOptions={tuitionOptions}
            paymentOptions={paymentOptions}
            schoolYearStartDate={schoolYearStartDate}
            schoolYearEndDate={schoolYearEndDate}
            updateFormData={form.updateAnswers}
          />
        )}
        {form.currentStepId === 'review' && (
          <ReviewStep
            formData={formData}
            updateFormData={form.updateAnswers}
          />
        )}
      </WizardFormLayout>

      {/* Decline Confirmation Dialog */}
      <AlertDialog open={showDeclineDialog} onOpenChange={setShowDeclineDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Your Response</AlertDialogTitle>
            <AlertDialogDescription>
              You've indicated that your family will not be returning next year.
              Are you sure you want to submit this response?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Go Back</AlertDialogCancel>
            <AlertDialogAction onClick={handleDecline} disabled={submitting}>
              {submitting ? 'Submitting...' : 'Yes, Submit'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Submit Confirmation Dialog */}
      <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit Re-enrollment?</AlertDialogTitle>
            <AlertDialogDescription>
              Once submitted, you won't be able to make changes. The school will review your
              re-enrollment and send you an enrollment packet to sign.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Submitting...' : 'Yes, Submit'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default ReenrollmentForm;
